// src/features/customers/components/CustomerMasterDataDashboard.tsx
//
// Menuepunkt "Kundenstammdaten": zeigt eine Liste aller Kunden und oeffnet
// bei Klick auf einen Kunden exakt dasselbe EditCustomerModal, das sonst
// ueber die Fristenliste (DeadlineDashboard) erreicht wird.
//
// Daten kommen aus customerService.getAllCustomers() (nicht geloeschte Kunden,
// inkl. zustaendigem Mitarbeiter per JOIN, sortiert nach Firmenname).
// Die Suche filtert clientseitig ueber Firmenname und Mandantennummer.

import React, { useEffect, useMemo, useState } from 'react';
import { customerService } from '../../../services/customer/CustomerService';
import { Customer } from '../../../shared/types';
import { EditCustomerModal } from './EditCustomerModal';

export const CustomerMasterDataDashboard: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showEditModal, setShowEditModal] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState<Customer | null>(null);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const data = await customerService.getAllCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Fehler beim Laden der Kunden:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const filteredCustomers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return customers;
    }
    return customers.filter((customer) => {
      const companyMatch = customer.company?.toLowerCase().includes(term) ?? false;
      const mandantMatch = customer.mandantennummer?.toLowerCase().includes(term) ?? false;
      return companyMatch || mandantMatch;
    });
  }, [customers, searchTerm]);

  // Gleiche Oeffnen/Schliessen-Logik wie im DeadlineDashboard, damit das
  // Stammdaten-Modal sich identisch zur Fristenliste verhaelt.
  const handleEditCustomer = (customer: Customer) => {
    setCustomerToEdit(customer);
    setShowEditModal(true);
  };

  // Erfolg beim Speichern: Das Modal bleibt bewusst geoeffnet, hier wird nur
  // die Kundenliste im Hintergrund aktualisiert. Das Schliessen passiert
  // ausschliesslich ueber X, Abbrechen bzw. Schliessen im Modal selbst.
  const handleEditSuccess = () => {
    loadCustomers();
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        color: '#6B7280'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid #E5E7EB',
          borderTop: '3px solid #3B82F6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginRight: '12px'
        }} />
        Lade Kunden...
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '24px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          margin: 0
        }}>
          Kundenstammdaten
        </h1>
      </div>

      {/* Suche (Firmenname oder Mandantennummer) */}
      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Suche nach Firmenname oder Mandantennummer..."
          style={{
            width: '100%',
            maxWidth: '420px',
            padding: '8px 12px',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Ergebnis-Anzahl */}
      <div style={{
        padding: '8px 12px',
        backgroundColor: '#EFF6FF',
        borderRadius: '6px',
        marginBottom: '16px',
        fontSize: '14px',
        color: '#1E40AF'
      }}>
        {searchTerm.trim()
          ? `Zeige ${filteredCustomers.length} von ${customers.length} Kunden`
          : `${customers.length} Kunden`}
      </div>

      {/* Kundenliste */}
      {filteredCustomers.length === 0 ? (
        <div style={{
          padding: '24px',
          textAlign: 'center',
          color: '#6B7280',
          backgroundColor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          fontSize: '14px'
        }}>
          Keine Kunden gefunden.
        </div>
      ) : (
        <div style={{
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#FFFFFF'
        }}>
          {filteredCustomers.map((customer, index) => (
            <div
              key={customer.id}
              onClick={() => handleEditCustomer(customer)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px',
                padding: '12px 16px',
                cursor: 'pointer',
                borderBottom: index < filteredCustomers.length - 1 ? '1px solid #F3F4F6' : 'none',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#111827'
                }}>
                  {customer.company}
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#6B7280',
                  marginTop: '2px'
                }}>
                  Berater {customer.beraternummer || '—'} · Mandant {customer.mandantennummer || '—'}
                </div>
              </div>
              <div style={{
                fontSize: '13px',
                color: '#374151',
                whiteSpace: 'nowrap'
              }}>
                {customer.zustaendiger_mitarbeiter?.full_name || '—'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stammdaten-Modal (identisch zur Fristenliste) */}
      {showEditModal && customerToEdit && (
        <EditCustomerModal
          isOpen={showEditModal}
          customer={customerToEdit}
          onClose={() => {
            setShowEditModal(false);
            setCustomerToEdit(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};
